'use client'

import { ICON_SVG_MAP } from '@/generated/icons'
import { type IconName } from '@/config/icons'

export type IconSize = 'sm' | 'md' | 'lg' | 'xl' | 'inherit'

interface IconProps extends React.HTMLAttributes<HTMLSpanElement> {
  icon: IconName
  /**
   * Visual size of the icon. Maps to a Tailwind `text-*` class applied to the
   * wrapper span, which constrains the SVG via `1em` so the icon renders at a
   * predictable size even when the parent doesn't set `font-size`. Use
   * `'inherit'` to opt out and let the icon scale with the parent's font-size.
   *
   * @default 'md'
   */
  size?: IconSize
  /** @deprecated Use `size` prop or className w-* h-* instead */
  width?: number | string
  /** @deprecated Use `size` prop or className w-* h-* instead */
  height?: number | string
}

const SIZE_CLASS: Record<IconSize, string> = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  inherit: '',
}

const TEXT_SIZE_OVERRIDE = /\btext-(xs|sm|base|lg|\d?xl)\b/

export function Icon({
  icon,
  size = 'md',
  className,
  width,
  height,
  style,
  ...props
}: IconProps) {
  const svg = ICON_SVG_MAP[icon]

  if (!svg) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Icon "${icon}" not found in generated icon map`)
    }
    return null
  }

  // Support legacy width/height props for backwards compatibility
  const sizeStyle = (width || height)
    ? { width: width ?? height, height: height ?? width, ...style }
    : style

  // Skip default size class if consumer already specified a text-* size — explicit
  // className intent wins over the default, regardless of Tailwind's class sort order.
  const hasTextOverride = !!className && TEXT_SIZE_OVERRIDE.test(className)
  const sizeClass = hasTextOverride ? '' : SIZE_CLASS[size]

  const wrapperClass = ['inline-flex items-center justify-center align-middle', sizeClass, className]
    .filter(Boolean)
    .join(' ')

  return (
    <span
      role="img"
      aria-hidden="true"
      className={wrapperClass}
      style={sizeStyle}
      dangerouslySetInnerHTML={{ __html: svg }}
      {...props}
    />
  )
}
